import { ITemplate, ITemplateField } from '../types/damTypes';

export interface TemplateFieldVisibilityOptions {
  showUploadFields?: boolean;
  showEditFields?: boolean;
  hideSeparators?: boolean;
}

export interface TemplateFieldValidationError {
  fieldId: string;
  fieldName: string;
  label: string;
  message: string;
}

const URL_FIELD_ERROR_MESSAGE = 'Ange en giltig absolut URL som borjar med http:// eller https://';

export function getVisibleTemplateFields(
  template: Pick<ITemplate, 'fields'> | null | undefined,
  {
    showUploadFields = true,
    showEditFields = true,
    hideSeparators = false,
  }: TemplateFieldVisibilityOptions = {},
): ITemplateField[] {
  if (!template?.fields?.length) {
    return [];
  }

  return template.fields.filter((field) => {
    if (field.type === 'separator') {
      if (hideSeparators) return false;
      if (showUploadFields && !showEditFields) return field.showDuringUpload;
      return true;
    }

    return (showUploadFields && field.showDuringUpload) || (showEditFields && field.showDuringEdit);
  });
}

export function getResolvedTemplateFieldValue(
  field: Pick<ITemplateField, 'id' | 'name' | 'defaultValue'>,
  values: Record<string, any>,
): any {
  const nameValue = field.name ? values[field.name] : undefined;
  let value = values[field.id];

  if ((value === undefined || value === '') && field.name) {
    value = nameValue;
  }

  if (value === undefined) {
    value = field.defaultValue;
  }

  return value;
}

export function getUrlFieldValidationError(value: any): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (typeof value !== 'string') {
    return URL_FIELD_ERROR_MESSAGE;
  }

  if (value.trim().length === 0) {
    return '';
  }

  if (value !== value.trim()) {
    return URL_FIELD_ERROR_MESSAGE;
  }

  try {
    const parsedUrl = new URL(value);
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return URL_FIELD_ERROR_MESSAGE;
    }

    return parsedUrl.origin === 'null' ? URL_FIELD_ERROR_MESSAGE : '';
  } catch {
    return URL_FIELD_ERROR_MESSAGE;
  }
}

export function getTemplateUrlValidationErrors(
  template: Pick<ITemplate, 'fields'> | null | undefined,
  values: Record<string, any>,
  visibilityOptions: TemplateFieldVisibilityOptions = {},
): TemplateFieldValidationError[] {
  return getVisibleTemplateFields(template, visibilityOptions)
    .filter((field) => field.type === 'url')
    .map((field) => {
      const message = getUrlFieldValidationError(getResolvedTemplateFieldValue(field, values));

      if (!message) {
        return null;
      }

      return {
        fieldId: field.id,
        fieldName: field.name,
        label: field.label,
        message,
      };
    })
    .filter((error): error is TemplateFieldValidationError => error !== null);
}
